/* gf-finder.js — GhostFit v4 School Finder
 * Vanilla IIFE. XSS-safe (createElement / textContent only — never innerHTML
 * for remote or user data). Two search backends, in priority order:
 *   1. [data-finder-proxy] -> same-origin POST proxy (schools-search service).
 *      ALL live searches go through the proxy; no search key ships to the
 *      browser and the roster is not browsable — a search fires only once the
 *      visitor has typed >= 4 characters of a school name.
 *   2. baked-in SAMPLE dataset (48 schools) so the section renders with no infra
 *
 * Result-card contract:
 *   - SAMPLE (inline) cards:
 *       { name, city, state, mascot, primaryColor, sportCount, status, shopifyPageHandle }
 *   - PROXY (Firestore-direct / Typesense) cards:
 *       { name, city, state, mascot, primaryColor, secondaryColor, sportCount,
 *         status, storeUrl, hasStore }
 *     hasStore=true  -> live store; link to storeUrl (full url), show status dot.
 *     hasStore=false -> no store yet; link to /pages/start-a-store, show a
 *                       "Start a store" affordance (most schools have no store).
 */
(function () {
  'use strict';

  /* Minimum typed characters (letters/digits) before a search fires. Below
     this the finder shows a hint instead of results — the roster is search-
     only, never browsable. Mirrors the server-side rule in schools-search. */
  var MIN_QUERY_ALNUM = 4;

  /* Live proxy searches are capped server-side at 8 results/page; the client
     must request the same page size so "Load more" pagination math lines up. */
  var PROXY_PER_PAGE_CAP = 8;

  /* Sample dataset: contract-shaped. handle derived from name+city+state. */
  var RAW = [
    ['Riverside High','Riverside','CA','Panthers','Basketball',3,'open','#D6541F'],
    ['Riverside High','Dublin','OH','Eagles','Football',1,'upcoming','#13452B'],
    ['Lincoln High','Lincoln','NE','Links','Football',2,'open','#A6192E'],
    ['Lincoln High','Stockton','CA','Trojans','Wrestling',1,'closed','#1C3F94'],
    ['Lincoln High','Tacoma','WA','Abes','Track & Field',1,'open','#5A1A33'],
    ['Westlake High','Austin','TX','Chaparrals','Football',4,'open','#1B264F'],
    ['Brentwood Academy','Brentwood','TN','Eagles','Football',2,'open','#7A0019'],
    ['Plano East Senior High','Plano','TX','Panthers','Soccer',2,'upcoming','#0A6E4C'],
    ['Carmel High','Carmel','IN','Greyhounds','Swimming',3,'open','#13294B'],
    ['Naperville Central','Naperville','IL','Redhawks','Volleyball',1,'open','#9E1B32'],
    ['New Trier High','Winnetka','IL','Trevians','Lacrosse',1,'closed','#0B5A3C'],
    ['Mater Dei High','Santa Ana','CA','Monarchs','Football',5,'open','#C8102E'],
    ['De La Salle High','Concord','CA','Spartans','Football',2,'open','#046A38'],
    ['Berkeley High','Berkeley','CA','Yellowjackets','Basketball',1,'upcoming','#C28800'],
    ['Highland Park High','Dallas','TX','Scots','Football',3,'open','#00467F'],
    ['Allen High','Allen','TX','Eagles','Football',2,'open','#C8102E'],
    ['Miami Senior High','Miami','FL','Stingarees','Baseball',1,'open','#00843D'],
    ['St. Thomas Aquinas','Fort Lauderdale','FL','Raiders','Football',4,'open','#7C2128'],
    ['Apopka High','Apopka','FL','Blue Darters','Football',2,'upcoming','#10284C'],
    ['Columbus High','Columbus','GA','Blue Devils','Baseball',1,'open','#1D2F6F'],
    ['Marietta High','Marietta','GA','Blue Devils','Football',2,'open','#003087'],
    ['Grady High','Atlanta','GA','Knights','Track & Field',1,'closed','#6D2077'],
    ['Massillon Washington','Massillon','OH','Tigers','Football',3,'open','#E06A13'],
    ['St. Ignatius High','Cleveland','OH','Wildcats','Hockey',2,'open','#003B5C'],
    ['Moeller High','Cincinnati','OH','Crusaders','Football',2,'upcoming','#0C2340'],
    ['Bishop Gorman','Las Vegas','NV','Gaels','Football',3,'open','#8B1A1A'],
    ['Brophy College Prep','Phoenix','AZ','Broncos','Baseball',1,'open','#7C2529'],
    ['Chaparral High','Scottsdale','AZ','Firebirds','Soccer',1,'upcoming','#C8102E'],
    ['Hamilton High','Chandler','AZ','Huskies','Football',2,'open','#841617'],
    ['Eden Prairie High','Eden Prairie','MN','Eagles','Hockey',3,'open','#13427B'],
    ['Wayzata High','Plymouth','MN','Trojans','Volleyball',1,'open','#0033A0'],
    ['Edina High','Edina','MN','Hornets','Hockey',2,'upcoming','#0E6B3D'],
    ['Mona Shores High','Muskegon','MI','Sailors','Football',1,'open','#003DA5'],
    ['East Grand Rapids','Grand Rapids','MI','Pioneers','Lacrosse',2,'open','#00573F'],
    ['Cass Technical High','Detroit','MI','Technicians','Basketball',2,'open','#00843D'],
    ['Mallard Creek High','Charlotte','NC','Mavericks','Football',2,'open','#00205B'],
    ['Myers Park High','Charlotte','NC','Mustangs','Soccer',1,'upcoming','#046A38'],
    ['Reagan High','Winston-Salem','NC','Raiders','Volleyball',1,'closed','#6A1B2A'],
    ['Bothell High','Bothell','WA','Cougars','Football',2,'open','#5B2A86'],
    ['Gonzaga Prep','Spokane','WA','Bullpups','Basketball',1,'open','#041E42'],
    ['Garfield High','Seattle','WA','Bulldogs','Basketball',3,'open','#4B2E83'],
    ['Fox Chapel Area','Pittsburgh','PA','Foxes','Lacrosse',1,'upcoming','#7A3E00'],
    ['Central Bucks West','Doylestown','PA','Bucks','Football',2,'open','#13294B'],
    ["Cardinal O'Hara",'Springfield','PA','Lions','Cheer',1,'open','#9B2242'],
    ['Iona Prep','New Rochelle','NY','Gaels','Basketball',2,'open','#74002A'],
    ['Fayetteville-Manlius','Manlius','NY','Hornets','Track & Field',1,'upcoming','#1A6E4F'],
    ['Christian Brothers Academy','Syracuse','NY','Brothers','Soccer',1,'closed','#4A1F70'],
    ['Bergen Catholic','Oradell','NJ','Crusaders','Wrestling',3,'open','#7C0A02']
  ];

  function handleize(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  var SAMPLE = RAW.map(function (r) {
    return {
      name: r[0], city: r[1], state: r[2], mascot: r[3],
      sport: r[4], sportCount: r[5], status: r[6], primaryColor: r[7],
      shopifyPageHandle: handleize(r[0] + '-' + r[1] + '-' + r[2])
    };
  });

  /* ---------------- helpers ---------------- */
  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function initials(name) {
    return String(name).replace(/[^A-Za-z ]/g, '').split(/\s+/).filter(Boolean)
      .slice(0, 2).map(function (w) { return w[0]; }).join('').toUpperCase();
  }

  function statusClass(s) { return s === 'open' ? '' : (s === 'upcoming' ? 'up' : 'cl'); }
  function statusText(s) { return s === 'open' ? 'Open now' : (s === 'upcoming' ? 'Opening soon' : 'Closed'); }

  function debounce(fn, ms) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  /* Count the letters/digits actually typed (spaces + punctuation don't count
     toward the minimum-query threshold). */
  function alnumCount(q) {
    var m = String(q == null ? '' : q).match(/[a-z0-9]/gi);
    return m ? m.length : 0;
  }

  /* ---------------- search backends ---------------- */
  /* All backends resolve to { hits:[card,...], found:Number }.
     card = result-card contract. */

  function makeProxyBackend(url) {
    return function (params) {
      // The live backend recognizes status 'open'/'none' (firestore-search.js).
      // Remap the legacy 'upcoming' value to 'none' (the storeless cohort) so the
      // status chip actually filters instead of being silently ignored.
      if (params && params.status === 'upcoming') {
        params = Object.assign({}, params, { status: 'none' });
      }
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params)
      }).then(function (r) { return r.json(); })
        .then(function (j) {
          // Contract returns both `results` and `hits` (same array); prefer
          // `hits` for back-compat, fall back to `results`.
          var hits = j.hits || j.results || [];
          return {
            hits: hits,
            found: typeof j.found === 'number' ? j.found : hits.length,
            facets: j.facets || null,
            proxy: true
          };
        });
    };
  }

  function makeSampleBackend() {
    var STATUS_RANK = { open: 0, upcoming: 1, closed: 2 };
    return function (params) {
      var q = (params.q || '').trim().toLowerCase();
      var list = SAMPLE.filter(function (s) {
        if (params.sport && s.sport !== params.sport) return false;
        if (params.status && s.status !== params.status) return false;
        if (q) {
          var hay = (s.name + ' ' + s.city + ' ' + s.mascot + ' ' + s.state + ' ' + s.sport).toLowerCase();
          if (hay.indexOf(q) < 0) return false;
        }
        return true;
      });
      if (params.sort === 'az') {
        list.sort(function (a, b) { return a.name.localeCompare(b.name) || a.city.localeCompare(b.city); });
      } else if (params.sort === 'open') {
        list.sort(function (a, b) { return STATUS_RANK[a.status] - STATUS_RANK[b.status] || b.sportCount - a.sportCount; });
      }
      var found = list.length;
      var start = (params.page - 1) * params.perPage;
      return Promise.resolve({ hits: list.slice(start, start + params.perPage), found: found });
    };
  }

  /* XSS-safe href guard for a remote storeUrl: allow only a same-origin path
     ("/...") or an absolute GhostFit URL ("https://ghostfitsports..."). Anything
     else (javascript:, data:, other hosts) is rejected -> treated as no store. */
  function safeStoreUrl(u) {
    if (!u) return null;
    var s = String(u).trim();
    if (s.charAt(0) === '/') return s;
    if (/^https:\/\/(www\.)?ghostfitsports\./i.test(s)) return s;
    return null;
  }

  /* ---------------- card rendering ---------------- */
  /* Prototype .scard markup, rendered as a clickable <a>. base = store link
     base path for SAMPLE cards (e.g. "/pages"). startUrl = where a storeless
     school sends shoppers ("/pages/start-a-store").
     Two shapes:
       - PROXY card (has hasStore): real Firestore school. hasStore -> link to
         storeUrl + status dot; !hasStore -> link to startUrl + "Start a store".
       - SAMPLE card (no hasStore): unchanged legacy behavior (handle -> base). */
  function buildCard(s, base, startUrl) {
    var a = el('a', 'scard');

    // Proxy cards carry an explicit hasStore flag; sample cards do not.
    var isProxy = Object.prototype.hasOwnProperty.call(s, 'hasStore');
    var hasStore = isProxy ? !!s.hasStore : (s.status !== 'closed');

    if (isProxy) {
      var url = hasStore ? safeStoreUrl(s.storeUrl) : null;
      if (url) {
        a.setAttribute('href', url);
      } else {
        // No store (or unsafe url) -> route to the start-a-store page.
        hasStore = false;
        a.setAttribute('href', startUrl || '/pages/start-a-store');
        a.classList.add('scard--nostore');
      }
    } else {
      var handle = s.shopifyPageHandle || handleize((s.name || '') + '-' + (s.city || '') + '-' + (s.state || ''));
      a.setAttribute('href', (base || '/pages').replace(/\/+$/, '') + '/' + encodeURIComponent(handle));
    }

    var crest = el('div', 'scard__crest', initials(s.name || '?'));
    crest.style.background = s.primaryColor || 'var(--accent)';
    a.appendChild(crest);

    var mid = el('div');
    mid.appendChild(el('div', 'scard__n', s.name || ''));
    var metaBits = [s.city, s.state].filter(Boolean).join(', ');
    if (s.mascot) metaBits += (metaBits ? ' · ' : '') + s.mascot;
    if (s.sport) metaBits += (metaBits ? ' · ' : '') + s.sport;
    mid.appendChild(el('div', 'scard__m', metaBits));
    a.appendChild(mid);

    var right = el('div', 'scard__r');
    if (isProxy && !hasStore) {
      // Storeless school: show a "Start a store" affordance instead of a
      // status dot — this school has no store yet.
      var start = el('span', 'scard__status start');
      start.appendChild(document.createTextNode('Start a store →'));
      right.appendChild(start);
      var so = Number(s.sportCount) || 0;
      if (so) right.appendChild(el('span', 'scard__stores', so + (so === 1 ? ' sport' : ' sports')));
    } else {
      var sc = statusClass(s.status);
      var status = el('span', 'scard__status' + (sc ? ' ' + sc : ''));
      status.appendChild(el('span', 'd'));
      status.appendChild(document.createTextNode(statusText(s.status)));
      right.appendChild(status);
      // Only show a count when one is provided (sample cards). Live-store proxy
      // cards are one-per-store and carry no count — don't render "0 stores".
      var n = Number(s.sportCount) || 0;
      if (n > 0) right.appendChild(el('span', 'scard__stores', n + (n === 1 ? ' store' : ' stores')));
    }
    a.appendChild(right);

    return a;
  }

  /* ---------------- controller ---------------- */
  function initRoot(root) {
    var perPageDefault = parseInt(root.getAttribute('data-per-page'), 10) || 20;
    var storeBase = root.getAttribute('data-store-base') || '/pages';
    var startUrl = root.getAttribute('data-start-url') || '/pages/start-a-store';

    var inputEl = $(root, '[data-finder-input]');
    var countEl = $(root, '[data-finder-count]');
    var sportWrap = $(root, '[data-finder-sport]');
    var statusWrap = $(root, '[data-finder-status]');
    var sortSel = $(root, '[data-finder-sort]');
    var summaryEl = $(root, '[data-finder-summary]');
    var gridEl = $(root, '[data-finder-grid]');
    var moreBtn = $(root, '[data-finder-more]');
    var recentEl = $(root, '[data-finder-recent]');
    var clearBtn = $(root, '[data-finder-clear]');

    /* pick backend — the proxy (schools-search service) is the ONLY live
       backend; the browser never talks to the search index directly. */
    var backend, isSample = false;
    if (root.getAttribute('data-finder-proxy')) {
      backend = makeProxyBackend(root.getAttribute('data-finder-proxy'));
    } else {
      backend = makeSampleBackend();
      isSample = true;
    }

    /* The Sport facet is only populated on the sample path; the live proxy
       backend returns facets:{state,status} only, so the chips would be a dead
       "All"-only column. Hide the whole Sport .facet block off the sample. */
    if (!isSample && sportWrap) {
      var sportFacet = sportWrap.closest ? sportWrap.closest('.facet') : null;
      if (sportFacet) sportFacet.hidden = true;
    }

    /* Live proxy = live-stores-only: every result is an open store, so the
       Store-status facet (All / Open now / No store yet) is meaningless. The
       server also pins the status filter away (anti-enumeration), so the chips
       could not filter anyway. Hide the whole block off the sample path. */
    if (!isSample && statusWrap) {
      var statusFacet = statusWrap.closest ? statusWrap.closest('.facet') : null;
      if (statusFacet) statusFacet.hidden = true;
    }

    /* On the live proxy path, request pages no larger than the server's cap so
       the "Load more" bookkeeping matches what actually comes back. */
    var perPage = isSample ? perPageDefault : Math.min(perPageDefault, PROXY_PER_PAGE_CAP);

    var state = { q: '', sport: '', status: '', sort: 'rel', page: 1, perPage: perPage };

    /* Deep-link support: prefill the search from a ?q= URL param so the homepage
       "find your school" band (GET /pages/find-your-school?q=...) and any shared
       link actually land on filtered results instead of dropping the query. */
    try {
      var urlQ = new URLSearchParams(window.location.search).get('q');
      if (urlQ) { state.q = urlQ.trim(); if (inputEl) inputEl.value = state.q; }
    } catch (e) { /* no-op */ }

    /* Locale-format a count with thousands separators (16,940 not 16940). */
    function fmtNum(n) {
      try { return Number(n).toLocaleString('en-US'); } catch (e) { return String(n); }
    }

    /* Populate the sport facet + recently-launched rail from sample data
       (only meaningful when running on the baked sample). */
    if (isSample) populateFacetsFromSample();

    function populateFacetsFromSample() {
      if (sportWrap && sportWrap.querySelectorAll('.fchip').length <= 1) {
        var sports = [];
        SAMPLE.forEach(function (s) { if (sports.indexOf(s.sport) < 0) sports.push(s.sport); });
        sports.sort().forEach(function (sp) {
          var b = el('button', 'fchip', sp);
          b.type = 'button';
          b.setAttribute('data-sport', sp);
          sportWrap.appendChild(b);
        });
      }

      if (recentEl && !recentEl.children.length) {
        var rec = SAMPLE.filter(function (s) { return s.status === 'upcoming'; })
          .concat(SAMPLE.filter(function (s) { return s.status === 'open'; })).slice(0, 6);
        rec.forEach(function (s) { recentEl.appendChild(buildCard(s, storeBase, startUrl)); });
      }
    }

    function setText(node, str) { if (node) node.textContent = str; }

    /* Empty the results grid and show the type-more hint. The finder never
       lists schools unprompted — results exist only for a typed name. */
    function renderHint() {
      while (gridEl.firstChild) gridEl.removeChild(gridEl.firstChild);
      var hint = el('div', 'find-empty',
        'Type your school’s name — at least ' + MIN_QUERY_ALNUM + ' letters — to search.');
      hint.style.cssText = 'grid-column:1/-1;padding:34px;text-align:center;color:var(--warm);border:1px dashed var(--line-2);border-radius:10px';
      gridEl.appendChild(hint);
      setText(countEl, '');
      if (summaryEl) { while (summaryEl.firstChild) summaryEl.removeChild(summaryEl.firstChild); }
      if (moreBtn) moreBtn.hidden = true;
    }

    function render() {
      /* Name-search only: nothing is fetched (and nothing renders) until the
         visitor has typed enough of a school name. */
      if (alnumCount(state.q) < MIN_QUERY_ALNUM) {
        renderHint();
        return;
      }
      var append = state.page > 1;
      backend({
        q: state.q, sport: state.sport, status: state.status,
        sort: state.sort, page: state.page, perPage: state.perPage
      }).then(function (res) {
        var hits = res.hits || [], found = res.found || 0;
        // When the proxy returns a status facet, surface how many of the matches
        // actually have a live store (most schools don't yet).
        var openCount = (res.facets && res.facets.status && typeof res.facets.status.open === 'number')
          ? res.facets.status.open : null;

        if (!append) {
          while (gridEl.firstChild) gridEl.removeChild(gridEl.firstChild);
        }

        if (!found && !append) {
          var empty = el('div', 'find-empty');
          empty.style.cssText = 'grid-column:1/-1;padding:34px;text-align:center;color:var(--warm);border:1px dashed var(--line-2);border-radius:10px';
          empty.appendChild(document.createTextNode('No store for that one yet — like most schools. '));
          var a = el('a', 'tlink', 'Start one for your team in minutes →');
          a.href = startUrl;
          a.style.color = 'var(--accent-deep)';
          empty.appendChild(a);
          gridEl.appendChild(empty);
          if (moreBtn) moreBtn.hidden = true;
        } else {
          hits.forEach(function (s) { gridEl.appendChild(buildCard(s, storeBase, startUrl)); });
          var shown = (state.page - 1) * state.perPage + hits.length;
          if (moreBtn) {
            if (shown < found) {
              moreBtn.hidden = false;
              moreBtn.textContent = 'Load more · showing ' + fmtNum(shown) + ' of ' + fmtNum(found);
            } else {
              moreBtn.hidden = true;
            }
          }
        }

        /* live counts + summary */
        setText(countEl, fmtNum(found) + (found === 1 ? ' school' : ' schools'));
        if (summaryEl) {
          while (summaryEl.firstChild) summaryEl.removeChild(summaryEl.firstChild);
          var b = el('b', null, fmtNum(found));
          summaryEl.appendChild(b);
          if (state.q) {
            summaryEl.appendChild(document.createTextNode(' ' + (found === 1 ? 'match' : 'matches') + ' for "' + state.q + '"'));
          } else {
            summaryEl.appendChild(document.createTextNode(' ' + (found === 1 ? 'school' : 'schools')));
          }
          // Most schools don't have a store yet — call out the live ones and
          // invite the rest to start one.
          if (openCount != null && found > 0) {
            if (openCount > 0) {
              summaryEl.appendChild(document.createTextNode(' · ' + openCount +
                (openCount === 1 ? ' with a live store' : ' with live stores')));
            } else {
              summaryEl.appendChild(document.createTextNode(' · none have a store yet — '));
              var sa = el('a', 'tlink', 'start one →');
              sa.setAttribute('href', startUrl);
              summaryEl.appendChild(sa);
            }
          }
        }
      }).catch(function (err) {
        if (window.console) console.error('[gf-finder] search failed', err);
        if (!append) {
          while (gridEl.firstChild) gridEl.removeChild(gridEl.firstChild);
          var errBox = el('div', 'find-empty', 'Search is temporarily unavailable. Please try again.');
          errBox.style.cssText = 'grid-column:1/-1;padding:34px;text-align:center;color:var(--warm);border:1px dashed var(--line-2);border-radius:10px';
          gridEl.appendChild(errBox);
        }
        if (moreBtn) moreBtn.hidden = true;
      });
    }

    function reset(p) { state.page = 1; if (p) state[p.k] = p.v; render(); }

    /* ---- wiring ---- */
    if (inputEl) {
      inputEl.addEventListener('input', debounce(function () {
        state.q = inputEl.value.trim();
        state.page = 1;
        render();
      }, 220));
    }

    if (sortSel) sortSel.addEventListener('change', function () { state.sort = sortSel.value; state.page = 1; render(); });

    if (sportWrap) sportWrap.addEventListener('click', function (e) {
      var b = e.target.closest('.fchip'); if (!b) return;
      $$(sportWrap, '.fchip').forEach(function (x) { x.classList.remove('on'); });
      b.classList.add('on');
      reset({ k: 'sport', v: b.getAttribute('data-sport') || '' });
    });

    if (statusWrap) statusWrap.addEventListener('click', function (e) {
      var b = e.target.closest('.fchip'); if (!b) return;
      $$(statusWrap, '.fchip').forEach(function (x) { x.classList.remove('on'); });
      b.classList.add('on');
      reset({ k: 'status', v: b.getAttribute('data-status') || '' });
    });

    if (moreBtn) moreBtn.addEventListener('click', function () { state.page += 1; render(); });

    if (clearBtn) clearBtn.addEventListener('click', function () {
      state = { q: '', sport: '', status: '', sort: 'rel', page: 1, perPage: perPage };
      if (inputEl) inputEl.value = '';
      if (sortSel) sortSel.value = 'rel';
      $$(sportWrap || root, '.fchip').forEach(function (x) { x.classList.toggle('on', x.getAttribute('data-sport') === ''); });
      $$(statusWrap || root, '.fchip').forEach(function (x) { x.classList.toggle('on', x.getAttribute('data-status') === ''); });
      render();
    });

    render();
  }

  function boot() {
    $$(document, '[data-finder-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
